import React from "react";
import { useTranslation } from "react-i18next";
import { useMutation } from "@apollo/client";
import { Formik, Form, FormikHelpers } from "formik";
import Select from "react-select";
import { useSelector } from "react-redux";
import RegionRegistrationSchema from "../registrationSteps/region/Region.schema";
import { IRegionSelection } from "../../models/interfaces/common";
import { getRegionAndLanguageData } from "../../utils/regionAndLanguagesGraphQL";
import { CHANGE_USER_INFO } from "../../services/graphql/migrations";
import { useAppDispatch } from "../../store";
import { RootState } from "../../rootReducer";
import { setUser } from "../../pages/auth/userSlice";
import { showAlert, showGreenAlert } from "@/util";

export default function Region() {
  const { t, i18n } = useTranslation("common");
  const dispatch = useAppDispatch();
  const UserInfoSlice = useSelector((state: RootState) => state.user);
  const [updateUserInfo] = useMutation(CHANGE_USER_INFO);

  const {
    getUpdatedLanguageOptions,
    allRegionList,
    allLanguageList,
    defaultRegion,
    defaultLanguage,
  } = getRegionAndLanguageData([]);

  const initialValues: IRegionSelection = {
    primaryRegion: defaultRegion || null,
    language: defaultLanguage || null,
  };

  const handleSubmit = async (
    values: IRegionSelection,
    { setSubmitting }: FormikHelpers<IRegionSelection>
  ) => {
    try {
      await updateUserInfo({
        variables: {
          id: Number(UserInfoSlice?.id),
          userInfo: {
            preferredLanguage: (values.language as any)?.value,
            primaryRegion: (values.primaryRegion as any)?.value,
          },
        },
      });

      const userInfo = {
        ...UserInfoSlice,
        preferredLanguage: (values.language as any)?.value,
        primaryRegion: (values.primaryRegion as any)?.value,
      };
      dispatch(setUser(userInfo));
      i18n.changeLanguage((values.language as any)?.value);
      showGreenAlert(t("Updated Successfully"));
    } catch (error) {
      console.error("Error updating user info:", error);
      showAlert(
        "error",
        "Error. Could not update language or region successfully"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={RegionRegistrationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, isSubmitting, setFieldValue, setFieldTouched }) => {
        // Get available languages for the selected region
        const availableLanguages =
          values.primaryRegion && getUpdatedLanguageOptions
            ? getUpdatedLanguageOptions(values.primaryRegion)
            : allLanguageList;

        return (
          <Form>
            <div className="row form-row">
              <div className="col-md-6 col-12">
                <div className="mb-4">
                  <label htmlFor="primaryRegion" className="form-label">
                    {t("Primary Region")}
                  </label>
                  <Select
                    classNamePrefix="regular-looking-dropdown"
                    className="w-100"
                    options={allRegionList}
                    isMulti={false}
                    name="primaryRegion"
                    placeholder="- Select Region -"
                    id="primaryRegion"
                    onChange={(option: any) => {
                      setFieldValue("primaryRegion", option);
                      setFieldTouched("primaryRegion", true);
                      // Clear language when region changes
                      setFieldValue("language", null);
                      setFieldTouched("language", false);
                      // Auto-select first available language
                      if (getUpdatedLanguageOptions && option) {
                        const languages = getUpdatedLanguageOptions(option);
                        if (languages && languages.length > 0) {
                          setFieldValue("language", languages[0]);
                        }
                      }
                    }}
                    onBlur={() => setFieldTouched("primaryRegion", true)}
                    value={values.primaryRegion}
                  />
                </div>
              </div>
              <div className="col-md-6 col-12">
                <div className="mb-4">
                  <label htmlFor="language" className="form-label">
                    {t("Language")}
                  </label>
                  <Select
                    classNamePrefix="regular-looking-dropdown"
                    className="w-100"
                    options={availableLanguages}
                    isMulti={false}
                    name="language"
                    placeholder="- Select Language -"
                    id="language"
                    onChange={(option: any) => {
                      setFieldValue("language", option);
                      setFieldTouched("language", true);
                    }}
                    onBlur={() => setFieldTouched("language", true)}
                    value={values.language}
                    isDisabled={!values.primaryRegion}
                  />
                </div>
              </div>
            </div>
            <div className="my-account-buttons">
              <button
                className="btn btn-lg btn-blue"
                type="submit"
                disabled={
                  isSubmitting || !values.primaryRegion || !values.language
                }
              >
                {t("Update")}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}
